import { encodePeriodValue } from '@components'

import { ItemsPerPage, useItemsPerPage } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'

import { useGlobalFiscalYear } from '../../../../../contexts/globalFiscalYearContext'
import { SortDirection } from '../../../../../enums/sortDirection'
import { useQueryParams } from '../../../../../hooks/useQueryParams'
import { getPeriodValueFromFiscalYear } from '../../../../../utils/getPeriodValueFromFiscalYear'
import { InvoicesSortProperty } from '../../../enums/invoicesSortProperty'
import { InvoiceStatus } from '../../../enums/invoiceStatus'
import { useInvoicesList } from './invoicesList'

export interface InvoicesListFilters {
  customerId?: string
  page?: number
  pageSize?: ItemsPerPage
  period?: string
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: InvoicesSortProperty
  status?: InvoiceStatus
}

export const defaultQuery: InvoicesListFilters = {
  page: 1,
  pageSize: undefined,
  period: undefined,
  searchQuery: '',
  sortDirection: SortDirection.Desc,
  sortProperty: InvoicesSortProperty.Date,
  status: InvoiceStatus.All,
}

type ContextState = [InvoicesListFilters, (filters: Partial<InvoicesListFilters>) => void]

export interface InvoicesListFiltersProviderProps {
  children?: ReactNode
  contextFilters?: Partial<InvoicesListFilters>
}

const InvoicesListFiltersContext = createContext<ContextState | undefined>(undefined)

export const InvoicesListFiltersProvider = ({
  children,
  contextFilters,
}: InvoicesListFiltersProviderProps): ReactElement => {
  const { options, organization } = useInvoicesList()
  const [pageSize] = useItemsPerPage()
  const { fiscalYear: globalFiscalYear } = useGlobalFiscalYear()
  const [queryParams, setQueryParams] = useQueryParams<InvoicesListFilters>({
    enabled: options.persistFiltersInUrl,
    defaultQuery: {
      ...defaultQuery,
      ...contextFilters,
      ...(globalFiscalYear && organization?.id
        ? { period: encodePeriodValue(getPeriodValueFromFiscalYear(globalFiscalYear), organization.id) }
        : {}),
      pageSize,
    },
  })

  const filters = useMemo(
    () => ({
      ...queryParams,
      // Sometimes `page` comes as a string. It needs to be always `number` type.
      page: Number(queryParams.page),
      // Sometimes `pageSize` comes as string. It needs to be always `number` type.
      pageSize: Number(queryParams.pageSize) as ItemsPerPage,
    }),
    [queryParams],
  )

  const setFilters = useCallback(
    (newFilters: Partial<InvoicesListFilters>) => {
      setQueryParams(newFilters)
    },
    [setQueryParams],
  )

  const value = useMemo((): ContextState => [filters, setFilters], [filters, setFilters])

  return <InvoicesListFiltersContext.Provider value={value}>{children}</InvoicesListFiltersContext.Provider>
}

export const useInvoicesListFilters = () => {
  const context = useContext(InvoicesListFiltersContext)

  if (!context) {
    throw new Error('InvoicesListFiltersContext is missing in the module!')
  }

  return context
}
